"use client";

import type { ComponentProps } from "react";
import {
  createContext,
  useCallback,
  useContext as useReactContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { Toasts } from "./components/Toasts";

/** Context */
export type ToastVariant = "success" | "error";

export type Toast = { message: string; variant: ToastVariant };

type Context = {
  setToast: (message: string, variant?: ToastVariant) => void;
  setToastAfterRefresh: (message: string, variant?: ToastVariant) => void;
  toast: null | Toast;
};

const context = createContext<Context>({
  setToast: () => {},
  setToastAfterRefresh: () => {},
  toast: null,
});

export const useContext = () => useReactContext(context);
/** */

const POST_RELOAD_KEY = "profile-post-reload";

export const Provider = ({
  children,
  ...props
}: Omit<ComponentProps<typeof context.Provider>, "value">) => {
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [toast, setToast] = useState<null | Toast>(null);

  useEffect(() => {
    if (typeof sessionStorage !== "undefined") {
      const plannedToast = JSON.parse(
        sessionStorage.getItem(POST_RELOAD_KEY) ?? "null",
      );
      if (plannedToast) {
        const { toast, variant } = plannedToast;
        if (toast?.length) {
          setToastWithTimeout(toast, variant);
        }
      }
      sessionStorage.removeItem(POST_RELOAD_KEY);
    }
  }, []);

  const setToastAfterRefresh = (
    toast: string,
    variant: ToastVariant = "success",
  ) => {
    /* this doesn't go through the hook above because we don't want to trigger the
     * useEffect BEFORE the refresh; but the useEffect will be triggered after the
     * refresh because of how the hook above works */
    sessionStorage.setItem(POST_RELOAD_KEY, JSON.stringify({ toast, variant }));
  };

  const setToastWithTimeout = useCallback(
    (message: string, variant: ToastVariant = "success") => {
      if (timeout.current !== null) clearTimeout(timeout.current);

      setToast({ message, variant });
      timeout.current = setTimeout(() => {
        setToast(null);
        timeout.current = null;
      }, 3500);
    },
    [],
  );

  return (
    <context.Provider
      value={{ setToast: setToastWithTimeout, toast, setToastAfterRefresh }}
      {...props}
    >
      {children}
      <Toasts />
    </context.Provider>
  );
};
